import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { tokenStorage } from '../lib/secureStorage';
import { authService } from '../services/apiService';
import { UserDto, UserRole, Permission } from '../types/user';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: UserDto | null;
  /** Reload auth state from storage; calls GET /auth/me when signed in. */
  refreshAuth: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CLAIM_NAMEIDENTIFIER = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';
const CLAIM_NAME = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
const CLAIM_ROLE = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

type JwtPayload = Record<string, unknown>;

function claimString(payload: JwtPayload, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.length > 0) return value;
    if (typeof value === 'number') return String(value);
  }
  return undefined;
}

function parsePermissionsClaim(raw: unknown): Permission[] {
  if (typeof raw !== 'string' || raw.length === 0) return [];
  return raw
    .split(',')
    .map((part) => parseInt(part.trim(), 10))
    .filter((n): n is Permission => !Number.isNaN(n));
}

/** Build UserDto from JWT payload (permissions from claim). */
function decodeUser(token: string | null): UserDto | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as JwtPayload;

    const userId = claimString(payload, CLAIM_NAMEIDENTIFIER, 'nameid', 'sub', 'id');
    const parsedId = userId ? parseInt(userId, 10) : NaN;
    if (!userId || Number.isNaN(parsedId)) return null;

    const username = claimString(payload, CLAIM_NAME, 'unique_name', 'name', 'sub');
    const roleValue = claimString(payload, CLAIM_ROLE, 'role');

    let role: UserRole = UserRole.User;
    let roleDisplayName = 'User';

    if (roleValue) {
      const roleMap: Record<string, UserRole> = {
        User: UserRole.User,
        Moderator: UserRole.Moderator,
        Admin: UserRole.Admin,
        SuperAdmin: UserRole.SuperAdmin,
      };
      if (roleValue in roleMap) {
        role = roleMap[roleValue];
        roleDisplayName = roleValue;
      } else {
        const numericRole = parseInt(roleValue, 10);
        if (!Number.isNaN(numericRole) && numericRole in UserRole) {
          role = numericRole as UserRole;
          const roleNames = ['User', 'Moderator', 'Admin', 'SuperAdmin'];
          roleDisplayName = roleNames[role] ?? 'User';
        }
      }
    }

    const permissions = parsePermissionsClaim(payload.permissions);

    return {
      id: parsedId,
      username: username ?? 'Unknown',
      role,
      roleDisplayName,
      roleDescription: '',
      permissions,
      createdAtUtc: '',
      isLocked: false,
      failedLoginAttempts: 0,
    };
  } catch {
    return null;
  }
}

function readAuthState() {
  const accessToken = tokenStorage.getAccessToken();
  const refreshToken = tokenStorage.getRefreshToken();
  return {
    token: accessToken,
    isAuthenticated: !!accessToken || !!refreshToken,
    user: decodeUser(accessToken),
  };
}

function mergeProfileWithJwt(jwtUser: UserDto | null, profile: UserDto): UserDto {
  if (!jwtUser) return profile;
  return {
    ...profile,
    permissions: profile.permissions.length > 0 ? profile.permissions : jwtUser.permissions,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState(readAuthState);

  const fetchProfile = useCallback((jwtUser: UserDto | null) => {
    void authService
      .getMe()
      .then((profile) => {
        setState((prev) => ({
          ...prev,
          user: mergeProfileWithJwt(jwtUser, profile),
        }));
      })
      .catch(() => {
        /* keep JWT user if profile fetch fails */
      });
  }, []);

  const refreshAuth = useCallback(() => {
    const next = readAuthState();
    setState(next);
    if (next.isAuthenticated && next.token) {
      fetchProfile(next.user);
    }
  }, [fetchProfile]);

  const logout = useCallback(() => {
    tokenStorage.clearTokens();
    setState({ token: null, isAuthenticated: false, user: null });
    window.location.href = '/login';
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === 'accessToken' || e.key === 'refreshToken') {
        refreshAuth();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshAuth]);

  const value = useMemo(
    () => ({
      isAuthenticated: state.isAuthenticated,
      token: state.token,
      user: state.user,
      refreshAuth,
      logout,
    }),
    [state, refreshAuth, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuthContext must be used within AuthProvider');
  return context;
};
